import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import { useAuth } from "./AuthContext";

const RightsContext = createContext();

export const RightsProvider = ({ children, connectionString }) => {
    const { user, selectedCompany, isSuperAdmin } = useAuth();

    const [rights, setRights] = useState(() => {
        try {
            const stored = sessionStorage.getItem("userRights");
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    });

    const [accessFlags, setAccessFlags] = useState(() => {
        try {
            const stored = sessionStorage.getItem("userAccessFlags");
            return stored ? JSON.parse(stored) : { canAccessWeb: true, canAccessMobile: true };
        } catch {
            return { canAccessWeb: true, canAccessMobile: true };
        }
    });

    const [rightsLoading, setRightsLoading] = useState(false);
    const [rightsReady, setRightsReady] = useState(() => !!sessionStorage.getItem("userRights"));
    const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);

    const fetchRights = useCallback(async (userId, compId) => {
        if (!userId || !compId) return;
        setRightsLoading(true);
        try {
            const res = await axios.get(
                `${connectionString}/api/user-rights/${userId}/${compId}`
            );
            if (res.data.success) {
                const normalized = {};
                (res.data.data || []).forEach((row) => {
                    normalized[row.ModuleKey] = {
                        view:            Boolean(row.CanView),
                        add:             Boolean(row.CanAdd),
                        edit:            Boolean(row.CanEdit),
                        delete:          Boolean(row.CanDelete),
                        downloadExcel:   Boolean(row.CanDownloadExcel),
                        viewPdf:         Boolean(row.CanViewPdf),
                    };
                });
                const flags = {
                    canAccessWeb:    res.data.canAccessWeb    !== undefined ? Boolean(res.data.canAccessWeb)    : true,
                    canAccessMobile: res.data.canAccessMobile !== undefined ? Boolean(res.data.canAccessMobile) : true,
                };
                setRights(normalized);
                setAccessFlags(flags);
                setRightsReady(true);
                sessionStorage.setItem("userRights", JSON.stringify(normalized));
                sessionStorage.setItem("userAccessFlags", JSON.stringify(flags));
            }
        } catch {
            // rights fetch failed — keep whatever is cached
        } finally {
            setRightsLoading(false);
        }
    }, [connectionString]);

    // Re-fetch whenever the logged-in user or selected company changes
    useEffect(() => {
        if (user?.UserId && selectedCompany?.CompId) {
            fetchRights(user.UserId, selectedCompany.CompId);
        } else if (!user) {
            // Logout — clear rights
            setRights({});
            setRightsReady(false);
            setAccessFlags({ canAccessWeb: true, canAccessMobile: true });
            sessionStorage.removeItem("userRights");
            sessionStorage.removeItem("userAccessFlags");
        }
    }, [user?.UserId, selectedCompany?.CompId, fetchRights]);

    const canAccess = useCallback(
        (moduleKey, action = "view") => {
            if (!moduleKey) return true;
            // Super Admin bypasses all rights checks
            if (isSuperAdmin) return true;
            const module = rights[moduleKey];
            if (!module) return false;
            return Boolean(module[action]);
        },
        [rights, isSuperAdmin]
    );

    const showAccessDenied = useCallback(() => {
        setAccessDeniedOpen(true);
    }, []);

    // Memoize context value to prevent unnecessary re-renders of entire app tree
    const contextValue = useMemo(() => ({
        rights,
        accessFlags,
        rightsLoading,
        rightsReady,
        canAccess,
        showAccessDenied,
        fetchRights,
    }), [rights, accessFlags, rightsLoading, rightsReady, canAccess, showAccessDenied, fetchRights]);

    return (
        <RightsContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={accessDeniedOpen}
                autoHideDuration={3000}
                onClose={() => setAccessDeniedOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setAccessDeniedOpen(false)}
                    sx={{ fontWeight: 600 }}
                >
                    Access Denied (403) — You don't have permission to perform this action.
                </Alert>
            </Snackbar>
        </RightsContext.Provider>
    );
};

export const useRights = () => useContext(RightsContext);
